<template>
  <div>
    <!-- Lexis Nexis error -->
    <v-alert
      dense
      text
      type="error"
      icon="mdi-alert-circle"
      class="margin-property-details-alert my-5 warning-text"
      v-if="userMustFill && !isReviewAlertShowing"
      ><span class="black--text"
        ><strong>Sorry!</strong> {{ warningMessage }}</span
      >
    </v-alert>

    <!-- Property Details questions -->
    <div :class="[formPropertyDetail.propertyType.Source?.toLowerCase()]">
      <BaseLabel :text="propertyDetailsText.PROPERTY_TYPE"></BaseLabel>
      <BaseRadio
        v-model="formPropertyDetail.propertyType.Value"
        pslStyle="image"
        :items="propertyTypeOptions"
        :rules="{
          required: {
            required: true,
            fieldLabel: LABEL_CONSTANTS.PROPERTY_TYPE_LABEL_MSG,
          },
        }"
        :className="'mb-9 property_type__wrapper'"
        @click="formUpdated(formPropertyDetail.propertyType)"
        aria-label="Property Type"
      />
    </div>

    <div :class="[formPropertyDetail.buildYear.Source?.toLowerCase()]">
      <BaseLabel
        :text="propertyDetailsText.BUILD_YEAR"
        :helpText="propertyDetailsText.BUILD_YEAR_HELP_TEXT"
      ></BaseLabel>
      <BaseInput
        vid="BuildYear"
        v-model="formPropertyDetail.buildYear.Value"
        mask="####"
        type="number"
        :pattern="'\\d*'"
        :inputmode="'numeric'"
        :dataUiid="DATA_UIID.buildYear"
        hideDetails="auto"
        className="input-build-year__wrapper remove-input-arrow mb-9"
        :rules="{
          required: {
            fieldLabel: LABEL_CONSTANTS.BUILD_YEAR_LABEL_MSG,
          },
          regex: {
            pattern: FOUR_DIGIT_NUMBER_PATTERN,
            customMessage: LABEL_CONSTANTS.BUILD_YEAR_FORMAT_MSG,
          },
          propertyYearInRange: {
            minYear: 999,
            customMessage: LABEL_CONSTANTS.BUILD_YEAR_RANGE_MSG,
          },
        }"
        :style="{
          'max-width': $vuetify.breakpoint.lgAndUp ? '200px' : '100%',
        }"
        :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        @keydown="formUpdated(formPropertyDetail.buildYear)"
        aria-label="Build Year"
      />
    </div>

    <div :class="[formPropertyDetail.numberOfBedrooms.Source?.toLowerCase()]">
      <BaseLabel
        :text="propertyDetailsText.NUMBER_OF_BEDROOMS"
        :helpText="propertyDetailsText.NUMBER_OF_BEDROOMS_HELP_TEXT"
      ></BaseLabel>
      <CounterBaseQuestion
        :dataUiid="DATA_UIID.numberOfBedrooms"
        :answerControlCounterOptions="ANSWER_CONTROL_BED_ROOM_COUNTER_OPTIONS"
        v-model="formPropertyDetail.numberOfBedrooms.Value"
        :className="'mb-9'"
        @change="formUpdated(formPropertyDetail.numberOfBedrooms)"
        aria-label="Number of Bedrooms"
      />
    </div>

    <div :class="[formPropertyDetail.numberOfBathrooms.Source?.toLowerCase()]">
      <BaseLabel
        :text="propertyDetailsText.NUMBER_OF_BATHROOMS"
        :helpText="propertyDetailsText.NUMBER_OF_BATHROOMS_HELP_TEXT"
      ></BaseLabel>
      <CounterBaseQuestion
        :dataUiid="DATA_UIID.numberOfBathrooms"
        :answerControlCounterOptions="ANSWER_CONTROL_COUNTER_OPTIONS"
        v-model="formPropertyDetail.numberOfBathrooms.Value"
        :className="'mb-4'"
        @change="formUpdated(formPropertyDetail.numberOfBathrooms)"
        aria-label="Number of Bathrooms"
      />
    </div>

    <v-alert
      color="var(--blue-100)"
      class="mb-9 note-warning border-sm border-solid border-info"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon>icon-bath</v-icon>
        </div>
        <div class="primary--text">
          <span class="font-weight-bold">Note:</span>&nbsp;{{
            propertyDetailsText.NUMBER_OF_BATHROOMS_NOTE
          }}
        </div>
      </div>
    </v-alert>

    <div :class="[formPropertyDetail.wallConstruction.Source?.toLowerCase()]">
      <BaseLabel
        :text="propertyDetailsText.WALL_CONSTRUCTION"
        :helpText="propertyDetailsText.WALL_CONSTRUCTION_HELP_TEXT"
      ></BaseLabel>
      <BaseSelect
        :dataUiid="DATA_UIID.wallConstruction"
        :rules="{
          required: {
            required: true,
            fieldLabel: LABEL_CONSTANTS.WALL_CONSTRUCTION_LABEL_MSG,
          },
        }"
        :items="wallConstructionOptions"
        v-model="formPropertyDetail.wallConstruction.Value"
        :dense="$vuetify.breakpoint.lgAndUp"
        :className="'mb-9 select__wrapper'"
        :style="{
          'max-width': $vuetify.breakpoint.lgAndUp ? '360px' : '100%',
        }"
        :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        @change="formUpdated(formPropertyDetail.wallConstruction)"
        aria-label="Wall Construction"
      />
    </div>

    <div :class="[formPropertyDetail.roofConstruction.Source?.toLowerCase()]">
      <BaseLabel
        :text="propertyDetailsText.ROOF_CONSTRUCTION"
        :helpText="propertyDetailsText.ROOF_CONSTRUCTION_HELP_TEXT"
      ></BaseLabel>
      <BaseSelect
        :dataUiid="DATA_UIID.roofConstruction"
        :rules="{
          required: {
            required: true,
            fieldLabel: LABEL_CONSTANTS.ROOF_CONSTRUCTION_LABEL_MSG,
          },
        }"
        :items="roofConstructionOptions"
        :isEnableCapitalizeLetterListItems="false"
        v-model="formPropertyDetail.roofConstruction.Value"
        :dense="$vuetify.breakpoint.lgAndUp"
        :className="'mb-9 select__wrapper'"
        :style="{
          'max-width': $vuetify.breakpoint.lgAndUp ? '360px' : '100%',
        }"
        :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        @change="formUpdated(formPropertyDetail.roofConstruction)"
        aria-label="Roof Construction"
      />
    </div>
  </div>
</template>

<script>
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseRadio from "@/components/BaseComponents/BaseRadio.vue";
import BaseSelect from "@/components/BaseComponents/BaseSelect.vue";
import CounterBaseQuestion from "@/components/BaseComponents/CounterBaseQuestion.vue";
import {
  ANSWER_CONTROL_BED_ROOM_COUNTER_OPTIONS,
  ANSWER_CONTROL_COUNTER_OPTIONS,
  PROPERTIES_TYPE_ITEMS,
  setItemDataUiids,
} from "@/constants/component.constants";
import {
  PROPERTY_DETAILS_ANSWER_MAP as DATA_UIID,
  PROPERTY_DETAILS_FORM,
} from "@/constants/form.constants";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import {
  FOUR_DIGIT_NUMBER_PATTERN,
  POST_CODE as POST_CODE_PATTERN,
} from "@/constants/regex-patterns.constants";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { mapGetters, mapState } from "vuex";
import { ANSWER_SOURCES } from "@/constants/answer.constants";

export default {
  name: "PropertyDetailsForm",
  data() {
    return {
      POST_CODE_PATTERN,
      FOUR_DIGIT_NUMBER_PATTERN,
      LABEL_CONSTANTS,
      DATA_UIID,
      PROPERTIES_TYPE_ITEMS,
      ANSWER_CONTROL_COUNTER_OPTIONS,
      ANSWER_CONTROL_BED_ROOM_COUNTER_OPTIONS,
      formPropertyDetail: PROPERTY_DETAILS_FORM,
      propertyDetailsTextDefault: {
        PROPERTY_TYPE: LABEL_CONSTANTS.PROPERTY_TYPE,
        BUILD_YEAR: LABEL_CONSTANTS.BUILD_YEAR,
        BUILD_YEAR_HELP_TEXT: LABEL_CONSTANTS.BUILD_YEAR_HELP_TEXT,
        NUMBER_OF_BEDROOMS: LABEL_CONSTANTS.NUMBER_OF_BEDROOMS,
        NUMBER_OF_BEDROOMS_HELP_TEXT:
          LABEL_CONSTANTS.NUMBER_OF_BEDROOMS_HELP_TEXT,
        NUMBER_OF_BATHROOMS: LABEL_CONSTANTS.NUMBER_OF_BATHROOMS,
        ROOF_CONSTRUCTION: LABEL_CONSTANTS.ROOF_CONSTRUCTION,
        ROOF_CONSTRUCTION_HELP_TEXT:
          LABEL_CONSTANTS.ROOF_CONSTRUCTION_HELP_TEXT,
        WALL_CONSTRUCTION: LABEL_CONSTANTS.WALL_CONSTRUCTION,
        WALL_CONSTRUCTION_HELP_TEXT:
          LABEL_CONSTANTS.WALL_CONSTRUCTION_HELP_TEXT,
        NUMBER_OF_BATHROOMS_NOTE: LABEL_CONSTANTS.NUMBER_OF_BATHROOMS_NOTE,
      },
    };
  },
  props: {
    propertyDetailsTextForm: {
      type: Object,
      required: false,
    },
    warningMessage: {
      type: String,
      default:
        "We're not able to find your property details at this time. Please enter your property details below.",
    },
    isReviewAlertShowing: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BaseInput,
    BaseLabel,
    BaseRadio,
    BaseSelect,
    CounterBaseQuestion,
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "propertyDataRequiresEdit",
      "roofConstructionItems",
      "wallConstructionItems",
    ]),
    ...mapState(QUESTIONS_MODULE, ["form", "questionMap"]),
    userMustFill() {
      // Shows the alert message if LN lookup failed
      return (
        this.form.propertyDetailsFormVisibility &&
        this.propertyDataRequiresEdit &&
        this.form.isLNErrorMessageEnabled
      );
    },
    propertyTypeOptions() {
      return setItemDataUiids("PropertyType", PROPERTIES_TYPE_ITEMS);
    },
    roofConstructionOptions() {
      return setItemDataUiids("RoofConstruction", this.roofConstructionItems);
    },
    wallConstructionOptions() {
      return setItemDataUiids("WallConstruction", this.wallConstructionItems);
    },
    propertyDetailsText() {
      return {
        ...this.propertyDetailsTextDefault,
        ...this.propertyDetailsTextForm,
      };
    },
  },
  beforeMount() {
    this.initDataFromStorage();
  },
  methods: {
    initDataFromStorage() {
      this.formPropertyDetail.propertyType = this.questionMap.PropertyType;
      this.formPropertyDetail.buildYear = this.questionMap.BuildYear;

      this.formPropertyDetail.numberOfBathrooms =
        this.questionMap.NumberOfBathrooms;
      const numberOfBathrooms = this.formPropertyDetail.numberOfBathrooms;
      if (numberOfBathrooms?.Value == null) {
        numberOfBathrooms.Value = "";
      }

      this.formPropertyDetail.numberOfBedrooms =
        this.questionMap.NumberOfBedrooms;
      const numberOfBedrooms = this.formPropertyDetail.numberOfBedrooms;
      if (numberOfBedrooms?.Value == null) {
        numberOfBedrooms.Value = "";
      }

      //Set both sources to ThirdParty if either bedrooms or bathrooms answer source is ThirdParty
      if (
        numberOfBathrooms?.Source === ANSWER_SOURCES.THIRD_PARTY ||
        numberOfBedrooms?.Source === ANSWER_SOURCES.THIRD_PARTY
      ) {
        numberOfBathrooms.Source = ANSWER_SOURCES.THIRD_PARTY;
        numberOfBedrooms.Source = ANSWER_SOURCES.THIRD_PARTY;
      }

      this.formPropertyDetail.wallConstruction =
        this.questionMap.WallConstruction;
      this.formPropertyDetail.roofConstruction =
        this.questionMap.RoofConstruction;
    },
    formUpdated(formProperty) {
      formProperty.Source = "User";
      this.$emit("update");
    },
  },
};
</script>

<style lang="scss" scoped>
.margin-property-details-alert {
  margin: 26px 0 0 0;
  :deep(.v-alert__icon) {
    margin-right: 2px;
  }
}

.property_type__wrapper {
  :deep(.v-messages__message) {
    margin-bottom: 10px;
  }
}
</style>
